import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDataProvider, useNotify, useQuery, useRefresh } from "react-admin";
import { Button, Modal, Select, Segmented, Space } from 'antd';
import ReactJson from "@microlink/react-json-view";
import { PlusCircleOutlined, EyeOutlined, CodeOutlined } from "@ant-design/icons";
import Editor from '@monaco-editor/react';
import { ExtendedDataProvider, VersionizedObjectsTabProps } from "../../types";
import { spinnerElement } from "@src/components/Common/utils";
import { ModalWidth } from "@src/components/Common/Variables";
import { DeployedItemViewer } from '@src/components/UniversalExecutor/DeployedItem';
import useGetGeneralSettings from "@src/providers/useGetGeneralSettings";
import { getServerErrorMessage } from "@src/helpers/serverErrors";

/**
 * One service item's versions.
 *
 * Everything below — the list, which version is selected, what the last write
 * returned, the modal and the JSON in it — is about the item in `record`, and
 * is meaningless for any other. Nothing here enforces that; the default export
 * does, by giving each service item an instance of its own.
 */
const ServiceItemVersions = ({ resource, light = false, name, attrName, isCreate, record, pov }: VersionizedObjectsTabProps) => {
    // The enhanced view only makes sense for deployed items produced by the Universal
    // Executor, which is a service owner concept: consumers always get the raw JSON.
    const isDeployedItem = name === "deployed item";

    /**
     * Which version is on screen, or null for "the newest, whichever that is".
     *
     * Null rather than a number on purpose: a write makes a new newest, and a
     * stored number would still be pointing at the one that was newest when it
     * was stored. Deriving it means the panel follows a new version in with
     * nothing to keep in step.
     */
    const [selected, setSelected] = useState<number | null>(null);
    /**
     * What the last write returned. The list is refetched after a write, but
     * that answer takes a moment and can arrive without the new version in it;
     * holding on to it means a slow or stale list cannot put an older version
     * back on screen in its place (#356).
     */
    const [created, setCreated] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState<'enhanced' | 'raw'>('enhanced');

    // The Universal Executor is part of the Pack feature set, so it is off wherever
    // that is off for the deployment — the same gate the NetOrca Pack tab uses.
    const generalSettings = useGetGeneralSettings();
    const aiEnabled = !!generalSettings
        .find((setting: any) => setting.name === 'AIIntegration')?.config?.show_ai_processor;

    const serviceId = record?.service?.id;
    const canHaveEnhancedView = isDeployedItem && pov === "serviceowner" && aiEnabled && !!serviceId;
    const [universalExecutorEnabled, setUniversalExecutorEnabled] = useState(false);
    const [packProfileLoaded, setPackProfileLoaded] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editorValue, setEditorValue] = useState<string>("{}");
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    /**
     * Whether this instance is still the one on screen.
     *
     * A request outlives the component that made it. Moving to another service
     * item replaces this instance, but a create that was already in flight
     * still resolves, and its handler still has a notify and a refresh to hand.
     * React drops a setState aimed at an instance that is gone; it has no say
     * over those two, and "Deployed item updated" is a lie about whichever item
     * the tab is showing by then.
     */
    const live = useRef(true);
    useEffect(() => () => { live.current = false; }, []);

    const { loaded, data, refetch } = useQuery({
        type: 'getList',
        resource: resource,
        payload: {
            pagination: { page: 1, perPage: 20 },
            // Newest first, and said so explicitly: the endpoint has no default
            // ordering of its own, so an ascending page 1 would leave the newest
            // version off the end of it altogether once there is more than a page.
            sort: { field: 'version', order: 'DESC' },
            filter: { service_item_id: record.id, light: light }
        }
    });

    /**
     * The Service Item endpoint already returns the latest deployed item on the
     * record, so seed the viewer with it. This shows the latest deployed item
     * immediately on the first tab open instead of flashing an empty object
     * while the versions list loads (#354).
     *
     * It arrives serialized as {data, version} rather than as the payload on its
     * own, which makes it the same shape as a row of the versions list — and that
     * is what lets the version label appear as early as the body does.
     */
    const seeded = useMemo(() => {
        const latest: any = isDeployedItem ? record?.deployed_item : null;
        if (!latest || typeof latest !== 'object' || Object.keys(latest).length === 0) return null;
        // tolerate a bare payload, in case the endpoint ever stops wrapping it
        return attrName in latest && 'version' in latest ? latest : { [attrName]: latest };
    }, [isDeployedItem, record, attrName]);

    /**
     * Newest first. The endpoint is asked for that order, but the ordering is
     * not part of its contract — and "which one is the latest" has to be right,
     * so it is decided here rather than assumed.
     */
    const versions: any[] = useMemo(() => {
        // a copy: `data` is the array held in the store, and sorting is in place
        const merged = [...((data as any[]) || [])];
        if (created) {
            const at = merged.findIndex((item: any) => item.id === created.id);
            if (at === -1) merged.push(created);
            else merged[at] = created;
        }
        return merged.sort((a: any, b: any) => (b.version ?? 0) - (a.version ?? 0));
    }, [data, created]);

    /* Until the list is in, the seeded latest stands in for it, so the panel has
       something to show and something to label it with from the first frame. */
    const shown: any[] = versions.length ? versions : seeded ? [seeded] : [];
    const current = (selected !== null && shown.find((item: any) => item.version === selected)) || shown[0] || null;
    const config = current ? current[attrName] : {};
    const options = shown
        .filter((item: any) => item.version != null)
        .map((item: any) => ({ value: item.version, label: `Version ${item.version}` }));

    useEffect(() => {
        if (!canHaveEnhancedView) {
            setUniversalExecutorEnabled(false);
            setPackProfileLoaded(true);
            return;
        }
        let cancelled = false;
        setPackProfileLoaded(false);
        dataProvider.getAction('serviceowner_pack_profile', {
            id: undefined,
            verb: `service/${serviceId}/config`
        })
            // strictly true: anything else — a missing field, a string, an error
            // payload — means we do not know it is on, so it is off
            .then(({ data }: any) => { if (!cancelled) setUniversalExecutorEnabled(data?.universal_executor_enabled === true); })
            .catch(() => { if (!cancelled) setUniversalExecutorEnabled(false); })
            .finally(() => { if (!cancelled) setPackProfileLoaded(true); });
        return () => { cancelled = true; };
    }, [canHaveEnhancedView, serviceId, dataProvider]);

    const openCreateModal = () => {
        setEditorValue(JSON.stringify(config ?? {}, null, 4));
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        // one click is one version: the modal stays open on failure, and a
        // further click while the first is in flight would write another row
        if (submitting) return;

        const trimmed = (editorValue || "").trim();
        if (!trimmed || trimmed === "{}") {
            notify("Empty dictionary is not accepted", "warning");
            return;
        }
        let parsed: any;
        try {
            parsed = JSON.parse(editorValue);
        } catch (e) {
            notify("Invalid JSON", "warning");
            return;
        }

        setSubmitting(true);
        dataProvider.create(resource, { data: { service_item: record.url, data: parsed } })
            .then(({ data: written }: any) => {
                // answered for an item the tab has since left: it has nothing to
                // say about the one in front of the user now
                if (!live.current) return;

                /* An unchanged payload is answered with 200 and the version that
                   is already there. The request succeeded and nothing was
                   written, so reporting it as an update would be the very thing
                   this tab is being fixed for (#356). */
                if (written?.message === 'no change detected') {
                    notify('No change detected — nothing was saved', 'warning');
                    return;
                }
                // what was just written is the newest, so follow the newest —
                // which is what a null selection means
                setCreated(written ?? null);
                setSelected(null);
                notify(name.charAt(0).toUpperCase() + name.slice(1) + ' updated', 'success');
                setIsModalOpen(false);
                refetch();
                refresh();
            })
            /* Leave the modal as it was: the JSON that was refused is the only
               copy of it, and the reason it was refused is usually something its
               author can fix in it. */
            .catch((error: any) => { if (live.current) notify(getServerErrorMessage(error, "Something went wrong"), "error"); })
            .finally(() => { if (live.current) setSubmitting(false); });
    };

    const isEmpty = !config || (typeof config === 'object' && Object.keys(config).length === 0);

    // Enabling the Universal Executor does not make the versions that came before
    // it its output. Only a version carrying what the viewer needs can be shown
    // enhanced; the rest are plain data, and plain data has nothing to enhance —
    // so there is no switch to offer either.
    const isExecutorData = !!config?.deployed_resources && !!config?.last_execution;
    const showViewSwitch = canHaveEnhancedView && universalExecutorEnabled && isExecutorData;
    const shouldShowEnhancedView = showViewSwitch && viewMode === 'enhanced';

    const renderVersionBody = () => {
        // Hold the panel until we know whether the switch applies, so the raw JSON
        // does not flash before the enhanced view takes over.
        if (canHaveEnhancedView && !packProfileLoaded) return spinnerElement;

        if (shouldShowEnhancedView)
            return <DeployedItemViewer data={config} />;

        if (isEmpty)
            return <div className="sid-empty">No data for this version.</div>;

        return (
            <div className="sid-json">
                <ReactJson
                    style={{ padding: '16px', background: 'transparent' }}
                    src={config}
                    displayDataTypes={false}
                    name={null}
                    displayArrayKey={false}
                    displayObjectSize={false}
                />
            </div>
        );
    };

    return (
        <div className="sid-deployed">
            {/* The count the list reports is the one it was given before the
                write, and a refetch does not clear it — so what there is to show
                is the only thing that can decide whether there is anything to
                show. Anything else hides a version that was just created. */}
            {!loaded && shown.length === 0 ? (
                spinnerElement
            ) : shown.length === 0 ? (
                <div className="sid-empty">No {name}s found.</div>
            ) : (
                <>
                    <div className="sid-toolbar">
                        <div className="sid-toolbar__left">
                            {/* every option already reads "Version n" */}
                            <Select
                                value={current?.version}
                                onChange={setSelected}
                                options={options}
                                style={{ minWidth: 170 }}
                                aria-label="Select version"
                            />
                        </div>
                        {showViewSwitch && (
                            <Segmented
                                value={viewMode}
                                onChange={(value) => setViewMode(value as 'enhanced' | 'raw')}
                                options={[
                                    {
                                        label: (
                                            <Space size="small">
                                                <EyeOutlined />
                                                Enhanced View
                                            </Space>
                                        ),
                                        value: 'enhanced'
                                    },
                                    {
                                        label: (
                                            <Space size="small">
                                                <CodeOutlined />
                                                Raw JSON
                                            </Space>
                                        ),
                                        value: 'raw'
                                    }
                                ]}
                            />
                        )}
                    </div>

                    {renderVersionBody()}
                </>
            )}

            {isCreate && (
                <>
                    <Button
                        onClick={openCreateModal}
                        style={{ marginTop: 16 }}
                        type="primary"
                        icon={<PlusCircleOutlined />}
                    >
                        {"Create/Modify " + name}
                    </Button>
                    <Modal
                        title={"Update " + name}
                        open={isModalOpen}
                        width={ModalWidth}
                        onCancel={() => setIsModalOpen(false)}
                        footer={[
                            <Button key="back" onClick={() => setIsModalOpen(false)}>Cancel</Button>,
                            /* Both props: `loading` is what stops the second
                               click, `disabled` is what says so to anything
                               reading the button rather than clicking it. */
                            <Button
                                key="submit"
                                type="primary"
                                onClick={handleCreate}
                                loading={submitting}
                                disabled={submitting}
                            >
                                Submit
                            </Button>
                        ]}
                    >
                        <div className="sid-editor">
                            <Editor
                                height="55vh"
                                defaultLanguage="json"
                                value={editorValue}
                                onChange={(value) => setEditorValue(value ?? "")}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    formatOnPaste: true,
                                    tabSize: 2,
                                    wordWrap: 'on',
                                }}
                            />
                        </div>
                    </Modal>
                </>
            )}
        </div>
    )
}

/**
 * A service item's worth of state, and not a version more.
 *
 * The route can move from one service item to the next without this tab ever
 * leaving the screen, and React Router hands the new one to the same component.
 * Everything above would then carry over: the version just written for the item
 * being left would be merged into the next one's list and, being the higher
 * number, shown as its latest; a version picked by hand would be looked for
 * among versions it was never chosen from; a half-written modal would still be
 * open over a different item's data.
 *
 * Keying on the id is what makes those states impossible rather than merely
 * corrected afterwards. An effect could only clear them once the render that
 * showed them had already happened; a new instance has nothing to clear,
 * because it starts at the initial state from its first frame.
 *
 * It settles the versions list too. ra-core's useQuery keeps its data in the
 * hook rather than in the store, and on a change of payload it sets `loading`
 * and leaves `data` and `loaded` exactly as they were until the new response
 * lands (ra-core 3.19 useQuery.js) — so an instance that survived the change of
 * item would go on showing the previous item's versions, as that item's, for as
 * long as the request took. A new instance has no previous response to keep,
 * and the old one's cannot reach it: ra-core's setState is a no-op once the
 * hook that owns it is unmounted (useSafeSetState).
 */
const VersionizedObjectsTab = (props: VersionizedObjectsTabProps) => (
    <ServiceItemVersions key={String(props.record?.id)} {...props} />
);

export default VersionizedObjectsTab;
